/**
 * AIPollingService - Global singleton for managing AI filter polling
 * 
 * This service runs independently of React components, ensuring AI processing
 * continues even when users navigate away from the leads page.
 * 
 * Features:
 * - Window-level polling (survives component unmounts)
 * - Multiple concurrent session support
 * - Browser notifications on completion
 * - Page refresh recovery via localStorage
 * - Event-based updates for React integration
 */

import { getAIFilterProgress, getAIFilterProgressCompanies } from '@/components/tabs/leads-v2/api'
import { getListAIProgress, type ListAIProgress } from '@/app/(app)/lists/api/listAIApi'
import { getFreshAccessToken } from '@/lib/supabase'

const STORAGE_KEY = 'cardinal_active_ai_sessions'
const POLL_INTERVAL = 2000 // 2 seconds
const NOTIFICATION_PREF_KEY = 'cardinal_ai_notifications_enabled'

export type SessionMode = 'people' | 'companies' | 'ai-column' | 'list-filter-leads' | 'list-filter-companies' | 'my-accounts-filter-leads' | 'my-accounts-filter-companies'

export interface SessionProgress {
    sessionId: string
    mode: SessionMode
    total: number
    analyzed: number
    matches: number
    isComplete: boolean
    status: string
    error?: string
    config?: Record<string, any>
    listId?: number
    currentLeadName?: string
    currentLeadTitle?: string
    currentLeadCompany?: string
    results?: any[]
}

export interface ActiveSessionInfo {
    sessionId: string
    mode: SessionMode
    sessionName: string
    progress: SessionProgress
    listId?: number
}

interface SessionPollState {
    sessionId: string
    mode: SessionMode
    sessionName?: string
    listId?: number
    intervalId?: NodeJS.Timeout
    lastProgress: SessionProgress | null
    callbacks: Set<(progress: SessionProgress) => void>
    errorCount: number
}

type SessionEventType = 'update' | 'complete' | 'error' | 'cancelled'

interface SessionEvent {
    type: SessionEventType
    sessionId: string
    progress: SessionProgress | null
    error?: string
}

class AIPollingServiceClass {
    private sessions: Map<string, SessionPollState> = new Map()
    private globalListeners: Set<(event: SessionEvent) => void> = new Set()
    private pendingCallbacks: Map<string, Set<(progress: SessionProgress) => void>> = new Map()
    private initialized = false

    constructor() {
        if (typeof window === 'undefined') return
        
        // Initialize on first construction
        this.init()
    }

    /**
     * Initialize the service - recover sessions from storage
     */
    private init() {
        if (this.initialized) return
        this.initialized = true

        // Recover sessions from localStorage
        this.recoverSessionsFromStorage()

        // Clean up on page unload
        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', () => {
                this.persistSessionsToStorage()
            })
        }
    }

    /**
     * Start polling for a session
     */
    startPolling(
        sessionId: string,
        mode: SessionMode,
        sessionName?: string,
        onUpdate?: (progress: SessionProgress) => void,
        listId?: number,
    ): void {
        console.log(`[AIPollingService] Starting polling for ${mode} session:`, sessionId)

        // If already actively polling (has interval), just add callback
        const existing = this.sessions.get(sessionId)
        if (existing?.intervalId != null) {
            if (onUpdate) {
                existing.callbacks.add(onUpdate)
            }
            console.log(`[AIPollingService] Already polling session ${sessionId}, added callback`)
            return
        }
        // If session exists but has no interval (completed run), remove it so we create a fresh session below
        if (existing) {
            this.sessions.delete(sessionId)
            this.pendingCallbacks.delete(sessionId)
        }

        // Collect all callbacks (provided + pending). Caller can pass onUpdate so we get updates even when subscription effect hasn't run yet.
        const allCallbacks = new Set<(progress: SessionProgress) => void>()
        if (onUpdate) {
            allCallbacks.add(onUpdate)
        }
        // Add any pending callbacks that were subscribed before polling started
        const pending = this.pendingCallbacks.get(sessionId)
        if (pending) {
            pending.forEach(cb => allCallbacks.add(cb))
            this.pendingCallbacks.delete(sessionId)
            console.log(`[AIPollingService] Added ${pending.size} pending callbacks for ${sessionId}`)
        }

        // Create new session state
        const sessionState: SessionPollState = {
            sessionId,
            mode,
            sessionName,
            listId,
            lastProgress: null,
            callbacks: allCallbacks,
            errorCount: 0,
        }

        this.sessions.set(sessionId, sessionState)

        // Start polling immediately
        this.pollSession(sessionState)

        // Set up interval
        const intervalId = setInterval(() => {
            this.pollSession(sessionState)
        }, POLL_INTERVAL)

        sessionState.intervalId = intervalId

        // Persist to storage
        this.persistSessionsToStorage()

        console.log(`[AIPollingService] Polling started for ${sessionId}, total sessions:`, this.sessions.size)
    }

    /**
     * Stop polling for a session
     */
    stopPolling(sessionId: string): void {
        const session = this.sessions.get(sessionId)
        if (!session) {
            console.log(`[AIPollingService] Session ${sessionId} not found, nothing to stop`)
            // Still clean up any pending callbacks
            this.pendingCallbacks.delete(sessionId)
            return
        }

        if (session.intervalId) {
            clearInterval(session.intervalId)
        }

        this.sessions.delete(sessionId)
        this.pendingCallbacks.delete(sessionId) // Clean up pending callbacks too
        this.persistSessionsToStorage()
        
        // Emit cancelled event to notify listeners that this session is gone
        this.emitEvent({
            type: 'cancelled',
            sessionId,
            progress: session.lastProgress
        })
        
        console.log(`[AIPollingService] Session removed, remaining sessions:`, this.sessions.size)
    }

    /**
     * Get current progress for a session (synchronous, from cache)
     */
    getSessionProgress(sessionId: string): SessionProgress | null {
        const session = this.sessions.get(sessionId)
        return session?.lastProgress || null
    }

    /**
     * Subscribe to session updates
     */
    subscribeToSession(sessionId: string, callback: (progress: SessionProgress) => void): void {
        const session = this.sessions.get(sessionId)
        if (session) {
            session.callbacks.add(callback)
            console.log(`[AIPollingService] Subscribed to existing session ${sessionId}, total callbacks: ${session.callbacks.size}`)
        } else {
            // Session doesn't exist yet - add to pending callbacks
            if (!this.pendingCallbacks.has(sessionId)) {
                this.pendingCallbacks.set(sessionId, new Set())
            }
            this.pendingCallbacks.get(sessionId)!.add(callback)
            console.log(`[AIPollingService] Added pending callback for ${sessionId}, total pending: ${this.pendingCallbacks.get(sessionId)!.size}`)
        }
    }

    /**
     * Unsubscribe from session updates
     */
    unsubscribeFromSession(sessionId: string, callback: (progress: SessionProgress) => void): void {
        const session = this.sessions.get(sessionId)
        if (session) {
            session.callbacks.delete(callback)
            console.log(`[AIPollingService] Unsubscribed from session ${sessionId}, remaining callbacks: ${session.callbacks.size}`)
        }
        // Also check pending callbacks
        const pending = this.pendingCallbacks.get(sessionId)
        if (pending) {
            pending.delete(callback)
            if (pending.size === 0) {
                this.pendingCallbacks.delete(sessionId)
            }
            console.log(`[AIPollingService] Removed from pending callbacks for ${sessionId}`)
        }
    }

    /**
     * Subscribe to all session events (global listener)
     */
    subscribeToAllSessions(callback: (event: SessionEvent) => void): void {
        this.globalListeners.add(callback)
    }

    /**
     * Unsubscribe from all session events
     */
    unsubscribeFromAllSessions(callback: (event: SessionEvent) => void): void {
        this.globalListeners.delete(callback)
    }

    /**
     * Get all active sessions with full information
     */
    getActiveSessions(): ActiveSessionInfo[] {
        return Array.from(this.sessions.values())
            .filter(s => s.lastProgress !== null)
            .map(s => ({
                sessionId: s.sessionId,
                mode: s.mode,
                sessionName: s.sessionName || 'AI Filter',
                progress: s.lastProgress!,
                listId: s.listId,
            }))
    }

    /**
     * Poll a single session
     */
    private isListAIMode(mode: SessionMode): boolean {
        return mode === 'ai-column' || mode === 'list-filter-leads' || mode === 'list-filter-companies' || mode === 'my-accounts-filter-leads' || mode === 'my-accounts-filter-companies'
    }

    private async pollSession(sessionState: SessionPollState): Promise<void> {
        const { sessionId, mode, callbacks } = sessionState

        // Skip polling if auth token isn't available yet (e.g. page-load recovery)
        const token = await getFreshAccessToken()
        if (!token) {
            console.warn(`[AIPollingService] No auth token yet for ${sessionId}, skipping poll`)
            return
        }

        try {
            let progressData: SessionProgress

            if (this.isListAIMode(mode)) {
                // List AI sessions use the /list-ai/progress endpoint
                const listProgress = await getListAIProgress(sessionId)
                progressData = {
                    sessionId,
                    mode,
                    total: listProgress.total,
                    analyzed: listProgress.processed,
                    matches: listProgress.match_count,
                    isComplete: listProgress.is_complete,
                    status: listProgress.status,
                    config: listProgress.config,
                    listId: listProgress.list_id,
                    currentLeadName: listProgress.current_lead_name,
                    currentLeadTitle: listProgress.current_lead_title,
                    currentLeadCompany: listProgress.current_lead_company,
                    // my-accounts sessions include results for building the match map
                    results: listProgress.results,
                }
            } else {
                const progress = mode === 'people'
                    ? await getAIFilterProgress(sessionId, 1, 25)
                    : await getAIFilterProgressCompanies(sessionId, 1, 25)
                progressData = {
                    sessionId,
                    mode,
                    total: progress.total,
                    analyzed: progress.analyzed,
                    matches: progress.matches,
                    isComplete: progress.isComplete,
                    status: progress.status,
                    config: progress.matchLimit ? { match_limit: progress.matchLimit } : undefined,
                    results: progress.results,
                }
            }

            // Update cached progress
            sessionState.lastProgress = progressData

            sessionState.errorCount = 0 // Reset error count on success

            // Notify callbacks
            console.log(`[AIPollingService] Notifying ${callbacks.size} callbacks for ${sessionId}`)
            callbacks.forEach(callback => {
                try {
                    callback(sessionState.lastProgress!)
                } catch (err) {
                    console.error(`[AIPollingService] Error in callback for ${sessionId}:`, err)
                }
            })

            // Notify global listeners
            this.emitEvent({
                type: 'update',
                sessionId,
                progress: sessionState.lastProgress
            })

            // Check if complete
            if (progressData.isComplete) {
                console.log(`[AIPollingService] Session ${sessionId} completed - keeping visible until dismissed`)
                
                // Stop the interval but keep the session in memory
                if (sessionState.intervalId) {
                    clearInterval(sessionState.intervalId)
                    sessionState.intervalId = undefined
                }
                
                // Emit completion event
                this.emitEvent({
                    type: 'complete',
                    sessionId,
                    progress: sessionState.lastProgress
                })

                // Show browser notification
                this.showNotification(sessionState)

                // Auto-dismiss completed session after 10 seconds
                setTimeout(() => {
                    // Only auto-dismiss if session is still in the map (user hasn't already dismissed)
                    if (this.sessions.has(sessionId)) {
                        console.log(`[AIPollingService] Auto-dismissing completed session ${sessionId}`)
                        this.stopPolling(sessionId)
                    }
                }, 10000)
            }

        } catch (error) {
            // 401 errors are expected during page-load recovery (auth not yet initialized)
            const is401 = error instanceof Error && error.message.includes('401')
            if (is401) {
                console.warn(`[AIPollingService] Auth not ready for ${sessionId}, will retry`)
                return
            }

            console.error(`[AIPollingService] Error polling session ${sessionId}:`, error)

            sessionState.errorCount++

            // If too many errors, stop polling
            if (sessionState.errorCount >= 5) {
                console.error(`[AIPollingService] Too many errors for ${sessionId}, stopping polling`)
                
                const errorProgress: SessionProgress = {
                    sessionId,
                    mode,
                    total: 0,
                    analyzed: 0,
                    matches: 0,
                    isComplete: true,
                    status: 'error',
                    error: String(error)
                }

                this.emitEvent({
                    type: 'error',
                    sessionId,
                    progress: errorProgress,
                    error: String(error)
                })

                this.stopPolling(sessionId)
            }
        }
    }

    /**
     * Emit event to global listeners
     */
    private emitEvent(event: SessionEvent): void {
        this.globalListeners.forEach(listener => {
            try {
                listener(event)
            } catch (err) {
                console.error('[AIPollingService] Error in global listener:', err)
            }
        })
    }

    /**
     * Show browser notification on completion
     */
    private showNotification(sessionState: SessionPollState): void {
        if (typeof window === 'undefined' || !('Notification' in window)) return

        // Check if notifications are enabled
        const notificationsEnabled = localStorage.getItem(NOTIFICATION_PREF_KEY) !== 'false'
        if (!notificationsEnabled) return

        const { lastProgress, sessionName, mode } = sessionState
        if (!lastProgress) return

        // Request permission if needed
        if (Notification.permission === 'default') {
            Notification.requestPermission().then(permission => {
                if (permission === 'granted') {
                    this.displayNotification(lastProgress, sessionName, mode)
                }
            })
        } else if (Notification.permission === 'granted') {
            this.displayNotification(lastProgress, sessionName, mode)
        }
    }

    /**
     * Display the actual notification
     */
    private displayNotification(progress: SessionProgress, sessionName: string | undefined, mode: string): void {
        const isListAI = this.isListAIMode(mode as SessionMode)
        const title = isListAI
            ? `AI Processing Complete${sessionName ? ': ' + sessionName : ''}`
            : `AI Filter Complete${sessionName ? ': ' + sessionName : ''}`
        const body = mode === 'ai-column'
            ? `Processed ${progress.analyzed} of ${progress.total} leads`
            : `Found ${progress.matches} matches out of ${progress.analyzed} ${mode} analyzed`

        const notification = new Notification(title, {
            body,
            icon: '/favicon.ico',
            badge: '/favicon.ico',
            tag: progress.sessionId,
            requireInteraction: false,
            silent: false
        })

        notification.onclick = () => {
            window.focus()
            if (isListAI && progress.listId) {
                window.location.href = `/contacts/lists/${progress.listId}`
            } else {
                window.location.href = '/leads-v2'
            }
            notification.close()
        }
    }

    /**
     * Enable/disable notifications
     */
    setNotificationsEnabled(enabled: boolean): void {
        localStorage.setItem(NOTIFICATION_PREF_KEY, String(enabled))
        
        if (enabled && typeof window !== 'undefined' && 'Notification' in window) {
            if (Notification.permission === 'default') {
                Notification.requestPermission()
            }
        }
    }

    /**
     * Check if notifications are enabled
     */
    areNotificationsEnabled(): boolean {
        if (typeof window === 'undefined' || !('Notification' in window)) return false
        return localStorage.getItem(NOTIFICATION_PREF_KEY) !== 'false' && Notification.permission === 'granted'
    }

    /**
     * Persist active sessions to localStorage
     */
    private persistSessionsToStorage(): void {
        if (typeof window === 'undefined') return

        const sessionData = Array.from(this.sessions.entries()).map(([sessionId, state]) => ({
            sessionId,
            mode: state.mode,
            sessionName: state.sessionName,
            listId: state.listId,
        }))

        localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData))
    }

    /**
     * Recover sessions from localStorage
     */
    private recoverSessionsFromStorage(): void {
        if (typeof window === 'undefined') return

        try {
            const stored = localStorage.getItem(STORAGE_KEY)
            if (!stored) return

            const sessionData = JSON.parse(stored) as Array<{
                sessionId: string
                mode: SessionMode
                sessionName?: string
                listId?: number
            }>

            console.log(`[AIPollingService] Recovering ${sessionData.length} sessions from storage`)

            // Start polling for each recovered session
            sessionData.forEach(({ sessionId, mode, sessionName, listId }) => {
                this.startPolling(sessionId, mode, sessionName, undefined, listId)
            })

        } catch (err) {
            console.error('[AIPollingService] Error recovering sessions from storage:', err)
            localStorage.removeItem(STORAGE_KEY)
        }
    }

    /**
     * Clear all sessions (for cleanup/testing)
     */
    clearAllSessions(): void {
        console.log('[AIPollingService] Clearing all sessions')
        
        this.sessions.forEach(session => {
            if (session.intervalId) {
                clearInterval(session.intervalId)
            }
        })

        this.sessions.clear()
        
        if (typeof window !== 'undefined') {
            localStorage.removeItem(STORAGE_KEY)
        }
    }
}

// Export singleton instance
export const AIPollingService = new AIPollingServiceClass()
